import { ThunderboltOutlined } from '@ant-design/icons'
import { Alert, Button, Empty, Segmented, Skeleton } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMembershipFlow } from '../contentProduction/canvas/membership/membershipFlowContext'
import MemberAllowanceTable from './MemberAllowanceTable'
import { CATEGORY_COLORS, DonutChart, HBars, TrendChart } from './teamCharts'
import { getCreditsOverview, teamErrorMessage } from './teamApi'
import { sharePercent } from './teamCreditsUsage'
import type { CreditsOverview, UsageRange } from './teamTypes'
import { useProviderLabel, useTeamFormat } from './teamUi'

/**
 * VistoeraTASK-00529 - 积分管理. Every number is aggregated by the backend from
 * the Workspace credit ledger / cached balance (one shared pool, no member
 * wallet). "0" (a real zero) and "no data" (empty state) are shown differently.
 */
export default function TeamCreditsTab({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation('team')
  const { num } = useTeamFormat()
  const providerLabel = useProviderLabel()
  const { openMembership } = useMembershipFlow()
  const [range, setRange] = useState<UsageRange>('30d')
  const [data, setData] = useState<CreditsOverview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getCreditsOverview(workspaceId, range)
      setData(res.data)
    } catch (e) {
      setError(teamErrorMessage(e, t('credits.loadFailed')))
    } finally {
      setLoading(false)
    }
  }, [workspaceId, range, t])

  useEffect(() => {
    void load()
  }, [load])

  const series = useMemo(() => {
    const trend = data?.trend ?? []
    return [
      { key: 'used', name: t('credits.trend.used'), values: trend.map((p) => Number(p.used) || 0), kind: 'line' as const, color: 'var(--tm-accent)' },
      { key: 'received', name: t('credits.trend.received'), values: trend.map((p) => Number(p.received) || 0), kind: 'line' as const, color: '#22c55e', dashed: true },
    ]
  }, [data, t])

  if (loading && !data) return <Skeleton active paragraph={{ rows: 10 }} />
  if (error && !data) {
    return (
      <Alert
        type="error"
        showIcon
        title={error}
        action={
          <Button size="small" onClick={() => void load()}>
            {t('common.retry')}
          </Button>
        }
      />
    )
  }
  if (!data) return null

  const catTotalCredits = data.by_category.reduce((s, c) => s + (Number(c.credits) || 0), 0)
  const sum = data.allowance_summary

  return (
    <div className="tm-credits" data-testid="team-credits">
      {error && <Alert type="warning" showIcon title={error} style={{ marginBottom: 12 }} />}

      <div className="tm-row tm-row-credit-top">
        <section className="tm-card tm-credit-card">
          <div className="tm-credit-head">
            <span className="tm-card-label">{t('credits.pool.title')}</span>
            <Button type="primary" icon={<ThunderboltOutlined />} onClick={openMembership} data-testid="team-credits-buy">
              {t('overview.credits.buy')}
            </Button>
          </div>
          <div className="tm-credit-balance" data-testid="team-credits-balance">
            {num(data.pool.balance)}
          </div>
          <div className="tm-hint">{t('credits.pool.sharedPool')}</div>
        </section>

        <div className="tm-card tm-metric">
          <span className="tm-card-label">{t('credits.pool.monthUsed')}</span>
          <strong className="tm-metric-value" data-testid="team-credits-month-used">
            {num(data.pool.month_used)}
          </strong>
          <span className="tm-hint">{t('credits.pool.monthTasks', { count: data.pool.month_tasks })}</span>
        </div>

        <div className="tm-card tm-metric">
          <span className="tm-card-label">{t('credits.pool.monthReceived')}</span>
          <strong className="tm-metric-value">{num(data.pool.month_received)}</strong>
          <span className="tm-hint">{t('credits.pool.receivedHint')}</span>
        </div>

        <div className="tm-card tm-metric">
          <span className="tm-card-label">{t('credits.allowance.title')}</span>
          {sum.limited_members > 0 ? (
            <>
              <strong className="tm-metric-value">{t('credits.allowance.limited', { count: sum.limited_members })}</strong>
              <span className="tm-hint">
                {t('credits.allowance.detail', { total: num(sum.total_limit), used: num(sum.used_by_limited), remaining: num(sum.remaining_of_limited) })}
              </span>
            </>
          ) : (
            <>
              <strong className="tm-metric-value">{t('credits.allowance.none')}</strong>
              <span className="tm-hint">{t('credits.allowance.noneHint', { count: sum.unlimited_members })}</span>
            </>
          )}
        </div>
      </div>

      <Alert type="info" showIcon title={t('credits.enforcementNote')} style={{ marginBottom: 16 }} data-testid="team-credits-enforcement-note" />

      <div className="tm-row tm-row-charts">
        <section className="tm-card tm-trend-card">
          <div className="tm-card-head">
            <span className="tm-card-title">{t('credits.trend.title')}</span>
            <Segmented<UsageRange>
              size="small"
              value={range}
              onChange={setRange}
              options={[
                { value: '7d', label: t('range.d7') },
                { value: '30d', label: t('range.d30') },
                { value: 'month', label: t('range.month') },
              ]}
            />
          </div>
          <TrendChart
            labels={data.trend.map((p) => p.date)}
            series={data.has_trend_data ? series : []}
            sharedScale
            format={(_k, v) => num(v)}
            ariaLabel={t('credits.trend.title')}
            emptyText={t('credits.trend.empty')}
          />
          {data.has_trend_data && (
            <div className="tm-chart-legend">
              {series.map((s) => (
                <span key={s.key}>
                  <i className="tm-dot" style={{ background: s.color }} />
                  {s.name}
                </span>
              ))}
            </div>
          )}
        </section>

        <section className="tm-card tm-dist-card">
          <div className="tm-card-head">
            <span className="tm-card-title">{t('credits.byType.title')}</span>
          </div>
          {data.by_category.length ? (
            <div className="tm-dist-body">
              <DonutChart
                ariaLabel={t('credits.byType.title')}
                items={data.by_category.map((c) => ({ key: c.key, value: Number(c.credits) || 0, color: CATEGORY_COLORS[c.key] }))}
              />
              <ul className="tm-legend">
                {data.by_category.map((c) => (
                  <li key={c.key}>
                    <span className="tm-dot" style={{ background: CATEGORY_COLORS[c.key] }} />
                    <span>{t(`overview.dist.${c.key}`)}</span>
                    <strong>{sharePercent(Number(c.credits) || 0, catTotalCredits)}%</strong>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('credits.noUsage')} />
          )}
        </section>

        <section className="tm-card tm-top-card">
          <div className="tm-card-head">
            <span className="tm-card-title">{t('credits.byProvider.title')}</span>
          </div>
          {data.by_provider.length ? (
            <HBars
              rows={data.by_provider.slice(0, 8).map((p) => ({
                key: p.provider_key ?? 'unknown',
                label: providerLabel(p.provider_key),
                value: Number(p.credits) || 0,
                right: t('credits.byProvider.row', { credits: num(p.credits), count: p.count }),
              }))}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('credits.noUsage')} />
          )}
        </section>
      </div>

      <MemberAllowanceTable workspaceId={workspaceId} onChanged={() => void load()} />
    </div>
  )
}
